import { VStack } from "styled-system/jsx";
import { HStack } from "styled-system/jsx";
import { Text } from "@ui/lib/park/ui/text";
import { TextTooltip } from "@ui/components/TextTooltip";
import { Icon } from "@ui/lib/park/ui/icon";
import { Info, LinkExternal } from "@ui/components/icons";
import { styled } from "styled-system/jsx";
import { useReadContract } from "wagmi";
import { useDataChainId } from "@ui/hooks/datachain";
import { getTokenAddress } from "@ui/services/contract/constants/address";
import SLVMON_ABI from "@ui/services/contract/abis/SLVMON";
import ISSUER_ABI from "@ui/services/contract/abis/Issuer";
import MON_STAKING_VAULT_ABI from "@ui/services/contract/abis/MONStakingVault";
import { formatUnits } from "viem";
import { Interval } from "@ui/constants/poll";
import { useQuery } from "@tanstack/react-query";
import { slvmonQuery } from "@ui/services/backend/hooks/slvmon/slvmonQuery";
import { issuer_raw } from "@ui/assets/config/issuer-raw";
import { getExplorerLink } from "@ui/utils/links";

export const Stats = () => {
    const chainId = useDataChainId();
    const slvmonAddress = getTokenAddress("sLVMON", chainId);
    const wmonAddress = getTokenAddress("WMON", chainId);

    const issuerAddress = issuer_raw[chainId]?.find(
        (config) => config.lvTokenSymbol === "LVMON"
    )?.issuerAddress;

    const { data: reserveConfig } = useReadContract({
        address: issuerAddress,
        abi: ISSUER_ABI,
        functionName: "reserveTokenConfig",
        args: wmonAddress ? [wmonAddress] : undefined,
        query: { enabled: !!issuerAddress && !!wmonAddress }
    });

    const treasuryVaultAddress = reserveConfig?.treasuryVault;

    const { data: stakingState } = useReadContract({
        address: treasuryVaultAddress,
        abi: MON_STAKING_VAULT_ABI,
        functionName: "stakingState",
        query: {
            enabled: !!treasuryVaultAddress,
            refetchInterval: Interval.SLOW
        }
    });

    // stakingState is [stakedBalance, lastEpoch]
    // @ts-ignore
    const stakedBalance = stakingState?.[0] || stakingState?.stakedBalance;

    const { data: totalAssets } = useReadContract({
        address: slvmonAddress,
        abi: SLVMON_ABI,
        functionName: "totalAssets",
        query: {
            enabled: !!slvmonAddress,
            refetchInterval: Interval.SLOW
        }
    });

    const { data: apy } = useQuery({
        queryKey: slvmonQuery.getAverageApyKey({ days: 7 }),
        queryFn: slvmonQuery.getAverageApyFn({ days: 7 }),
        refetchInterval: Interval.SLOW,
    });

    const APY_DISPLAY_CAP = 1500;
    const apyValue = typeof apy === "number" ? apy : null;
    const formattedApy = apyValue === null
        ? "-"
        : apyValue > APY_DISPLAY_CAP
            ? `> ${APY_DISPLAY_CAP}%`
            : `${apyValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
    const apyTooltipContent = apyValue === null
        ? "Annual Percentage Yield"
        : `Annual Percentage Yield (Actual: ${apyValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}%)`;

    const formattedTotalAssets = totalAssets 
        ? Number(formatUnits(totalAssets, 18)).toLocaleString(undefined, { maximumFractionDigits: 0 })
        : "-";

    const formattedStakedBalance = stakedBalance
        ? Number(formatUnits(stakedBalance, 18)).toLocaleString(undefined, { maximumFractionDigits: 0 })
        : "-";

    return (
        <VStack gap={2} w="full">
            <HStack
                justifyContent="space-between"
                w="full"
                borderRadius="4px"
                bg="#222222"
                p={3}
            >
                <HStack gap={1}>
                    <Text color="greyWhite.grey" fontSize={14}>
                        APY:
                    </Text>
                    <TextTooltip
                        content={apyTooltipContent}
                        triggerCss={{
                            textDecoration: 'none',
                            cursor: 'help',
                        }}
                    >
                        <Icon color="greyWhite.grey" boxSize="14px">
                            <Info />
                        </Icon>
                    </TextTooltip>
                </HStack>
                <Text color="#D4F82D" fontSize={14} fontWeight="bold">
                    {formattedApy}
                </Text>
            </HStack>

            <HStack
                justifyContent="space-between"
                w="full"
                borderRadius="4px"
                bg="#222222"
                p={3}
            >
                <Text color="greyWhite.grey" fontSize={14}>
                    Total staked LVMON:
                </Text>
                <HStack gap={1}>
                    <styled.img src="/tokens/LVMON.svg" width="16px" height="16px" />
                    <Text color="white" fontSize={14} fontWeight="bold">
                        {formattedTotalAssets}
                    </Text>
                </HStack>
            </HStack>

            <VStack
                w="full"
                borderRadius="4px"
                bg="#222222"
                p={3}
                gap={2}
            >
                <HStack w="full" mb={1}>
                    <Text color="greyWhite.grey" fontSize={14}>
                        Protocol Interest-Bearing Assets:
                    </Text>
                </HStack>
                <HStack justifyContent="space-between" w="full">
                    <Text color="greyWhite.grey" fontSize={14}>
                        gMON:
                    </Text>
                    <HStack gap={1}>
                        <styled.img src="/tokens/MON.svg" width="16px" height="16px" />
                        <Text color="white" fontSize={14} fontWeight="bold">
                            -
                        </Text>
                    </HStack>
                </HStack>
                <HStack justifyContent="space-between" w="full">
                    <Text color="greyWhite.grey" fontSize={14}>
                        shMON:
                    </Text>
                    <HStack gap={1}>
                        <styled.img src="/tokens/MON.svg" width="16px" height="16px" />
                        <Text color="white" fontSize={14} fontWeight="bold">
                            -
                        </Text>
                    </HStack>
                </HStack>
                <HStack justifyContent="space-between" w="full">
                    <HStack gap={1} alignItems="center">
                        <Text color="greyWhite.grey" fontSize={14}>
                            delegate MON:
                        </Text>
                        {treasuryVaultAddress && (
                            <TextTooltip
                                content="View on explorer"
                                triggerCss={{ textDecoration: 'none' }}
                                contentCss={{ p: '4px 6px' }}
                            >
                                <Icon
                                    onClick={() => {
                                        window.open(
                                            getExplorerLink(treasuryVaultAddress, 'stake-portfolio', chainId),
                                            '_blank',
                                        );
                                    }}
                                    cursor="pointer"
                                    boxSize="14px"
                                    color="greyWhite.grey"
                                    _hover={{ color: 'white' }}
                                >
                                    <LinkExternal />
                                </Icon>
                            </TextTooltip>
                        )}
                    </HStack>
                    <HStack gap={1}>
                        <styled.img src="/tokens/MON.svg" width="16px" height="16px" />
                        <Text color="white" fontSize={14} fontWeight="bold">
                            {formattedStakedBalance}
                        </Text>
                    </HStack>
                </HStack>
            </VStack>
        </VStack>
    )
}